import React, { useState, useEffect } from 'react'
import { 
  ChartBarIcon, 
  CalendarIcon,
  DocumentArrowDownIcon,
  // EyeIcon,
  // FunnelIcon,
  CurrencyDollarIcon,
  ShoppingCartIcon,
  ClockIcon,
  UserGroupIcon,
  UserIcon,
  GiftIcon
} from '@heroicons/react/24/outline'
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  AreaChart,
  Area
} from 'recharts'
import { apiService } from '../services/api'
import * as XLSX from 'xlsx'

interface ReportData {
  salesData: any[]
  productData: any[]
  customerData: any[]
  timeData: any[]
  paymentData: any[]
  summary: {
    totalRevenue: number
    totalOrders: number
    avgOrderValue: number
    totalCustomers: number
    topProduct: string
    peakHour: string
  }
}

interface ClosingReportRow {
  date: string
  grossSales?: number
  netSales?: number
  finalNetSales?: number
  totalSaleAfterDiscount: number
  totalSale?: number
  cashSales: number
  cardSales: number
  cateringSales: number
  discountAmount: number
  creditDinnerAmount?: number
  taxCollectedAmount?: number
  refundAmount?: number
}

interface StaffOrdersReportData {
  byStaff: Array<{
    created_by: string | null
    staff_name: string
    order_count: number
    total_amount: number
  }>
  orders: Array<{
    id: string
    order_number: string
    order_time: string
    order_source?: string
    total_amount: number
    status: string
    placed_by_name: string
  }>
  startISO: string
  endISO: string
}

interface ClosingReportData {
  rows: ClosingReportRow[]
  summary: {
    openingCash: number
    grossSales?: number
    netSales?: number
    finalNetSales?: number
    discounts?: number
    totalSale: number
    totalSaleAfterDiscount: number
    cashSales: number
    cardSales: number
    cateringSales: number
    discountAmount: number
    creditDinnerAmount?: number
    creditDinnerTransactionCount?: number
    cashTransactionCount: number
    cardTransactionCount: number
    taxCollectedAmount: number
    discountTransactionCount: number
    refundTransactionCount: number
    refundAmount: number
  }
  startISO: string
  endISO: string
  creditDinnerEnabled?: boolean
  metricVersion?: number
  definitions?: Record<string, string>
}

interface CreditDinnerReportData {
  byCo: Array<{ co_name: string; order_count: number; total_credit_amount: number }>
  orders: Array<{
    id: string
    order_number: string
    order_time: string
    order_source?: string
    total_amount: number
    discount_amount?: number
    status: string
    payment_method?: string
    credit_dinner_co_name?: string | null
    credit_dinner_customer_display?: string | null
    credit_dinner_amount?: number | null
    customer_name?: string | null
    placed_by_name?: string
  }>
  startISO: string
  endISO: string
}

interface RestaurantSettings {
  currency: string
  currency_symbol: string
  currency_display_type: 'text' | 'icon'
  currency_display_position: 'left' | 'right'
  currency_icon_url: string
}

// StatCard component used in reports sections
const StatCard = ({ title, value, icon: Icon, change: _change, changeType: _changeType, color = 'blue', isCurrency = false, formatCurrency }: any) => (
  <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
    <div className="p-5">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
        <div className="ml-5 w-0 flex-1">
          <dl>
            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
              {title}
            </dt>
            <dd className="text-lg font-medium text-gray-900 dark:text-white">
              {isCurrency && typeof value === 'number' && formatCurrency ? formatCurrency(value) : (typeof value === 'number' && value > 1000 ? value.toLocaleString() : value)}
            </dd>
          </dl>
        </div>
      </div>
    </div>
  </div>
)

const Reports: React.FC = () => {
  const [reportData, setReportData] = useState<ReportData | null>(null)
  const [dateRange, setDateRange] = useState<'today' | 'week' | 'month' | 'year' | 'custom'>('week')
  const [customStartDate, setCustomStartDate] = useState('')
  const [customEndDate, setCustomEndDate] = useState('')
  const [selectedReport, setSelectedReport] = useState<
    'sales' | 'products' | 'customers' | 'time' | 'closing' | 'staff' | 'creditDinner'
  >('sales')
  const [loading, setLoading] = useState(false)
  const [closingReportData, setClosingReportData] = useState<ClosingReportData | null>(null)
  const [staffOrdersData, setStaffOrdersData] = useState<StaffOrdersReportData | null>(null)
  const [creditDinnerReportData, setCreditDinnerReportData] = useState<CreditDinnerReportData | null>(null)
  const [creditDinnerEnabled, setCreditDinnerEnabled] = useState(false)
  const [creditDinnerCoInput, setCreditDinnerCoInput] = useState('')
  const [creditDinnerCustomerInput, setCreditDinnerCustomerInput] = useState('')
  const [creditDinnerCoApplied, setCreditDinnerCoApplied] = useState('')
  const [creditDinnerCustomerApplied, setCreditDinnerCustomerApplied] = useState('')
  const [restaurantSettings, setRestaurantSettings] = useState<RestaurantSettings>({
    currency: 'SAR',
    currency_symbol: 'ر.س',
    currency_display_type: 'text',
    currency_display_position: 'left',
    currency_icon_url: ''
  })

  useEffect(() => {
    loadRestaurantSettings()
    loadReportData()
  }, [dateRange, customStartDate, customEndDate, selectedReport, creditDinnerCoApplied, creditDinnerCustomerApplied])

  useEffect(() => {
    const loadPos = async () => {
      try {
        const r = await apiService.getPOSSettings()
        if (r.success && r.data) {
          setCreditDinnerEnabled(!!(r.data as { creditDinnerEnabled?: boolean }).creditDinnerEnabled)
        }
      } catch {
        setCreditDinnerEnabled(false)
      }
    }
    void loadPos()
  }, [])

  useEffect(() => {
    if (selectedReport === 'creditDinner' && !creditDinnerEnabled) {
      setSelectedReport('sales')
    }
  }, [creditDinnerEnabled, selectedReport])

  const loadRestaurantSettings = async () => {
    try {
      const response = await apiService.getRestaurantSettings()
      if (response.success && response.data) {
        const settings = response.data as unknown as RestaurantSettings
        setRestaurantSettings({
          currency: settings.currency || 'SAR',
          currency_symbol: settings.currency_symbol || 'ر.س',
          currency_display_type: settings.currency_display_type || 'text',
          currency_display_position: settings.currency_display_position || 'left',
          currency_icon_url: settings.currency_icon_url || ''
        })
      }
    } catch (error) {
      console.error('Error loading restaurant settings:', error)
    }
  }

  // Helper function to format currency with display options
  // Uses currency_symbol from restaurant settings
  const formatCurrency = (amount: number) => {
    const formattedAmount = parseFloat(amount.toFixed(2)).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
    
    // Get currency symbol from settings
    const currencySymbol = restaurantSettings.currency_symbol || 'ر.س'
    
    if (restaurantSettings.currency_display_type === 'icon' && restaurantSettings.currency_icon_url) {
      // Use PNG icon
      if (restaurantSettings.currency_display_position === 'left') {
        return (
          <>
            <img src={restaurantSettings.currency_icon_url} alt="Currency" className="inline-block w-4 h-4 mr-1" />
            {formattedAmount}
          </>
        )
      } else {
        return (
          <>
            {formattedAmount}
            <img src={restaurantSettings.currency_icon_url} alt="Currency" className="inline-block w-4 h-4 ml-1" />
          </>
        )
      }
    } else {
      // Use text symbol from settings
      if (restaurantSettings.currency_display_position === 'left') {
        return `${currencySymbol}${formattedAmount}`
      } else {
        return `${formattedAmount}${currencySymbol}`
      }
    }
  }

  const loadReportData = async () => {
    // Don't load data for custom range if dates are not set
    if (dateRange === 'custom' && (!customStartDate || !customEndDate)) {
      return
    }
    
    setLoading(true)
    
    try {
      const params: { dateRange: string; startDate?: string; endDate?: string } = {
        dateRange: dateRange
      }
      
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        params.startDate = customStartDate
        params.endDate = customEndDate
      }

      if (selectedReport === 'staff') {
        const response = await apiService.getStaffOrdersReport(params)
        if (response.success && response.data) {
          setStaffOrdersData(response.data as StaffOrdersReportData)
        } else {
          setStaffOrdersData({ byStaff: [], orders: [], startISO: '', endISO: '' })
        }
        setClosingReportData(null)
        setCreditDinnerReportData(null)
        return
      }

      if (selectedReport === 'creditDinner') {
        if (!creditDinnerEnabled) {
          setCreditDinnerReportData(null)
          return
        }
        const response = await apiService.getCreditDinnerReport({
          ...params,
          coName: creditDinnerCoApplied || undefined,
          customerName: creditDinnerCustomerApplied || undefined
        })
        if (response.success && response.data) {
          setCreditDinnerReportData(response.data as CreditDinnerReportData)
        } else {
          setCreditDinnerReportData({ byCo: [], orders: [], startISO: '', endISO: '' })
        }
        setStaffOrdersData(null)
        setClosingReportData(null)
        return
      }

      setStaffOrdersData(null)
      setCreditDinnerReportData(null)

      const response = selectedReport === 'closing'
        ? await apiService.getClosingReport(params)
        : await apiService.getReports(params)
      
      if (response.success && response.data) {
        if (selectedReport === 'closing') {
          setClosingReportData(response.data as ClosingReportData)
        } else {
          setReportData(response.data as ReportData)
        }
      } else {
        console.error('Error loading reports:', response.error)
        if (selectedReport === 'closing') {
          setClosingReportData({
            rows: [],
            summary: {
              openingCash: 0,
              grossSales: 0,
              netSales: 0,
              finalNetSales: 0,
              discounts: 0,
              totalSale: 0,
              totalSaleAfterDiscount: 0,
              cashSales: 0,
              cardSales: 0,
              cateringSales: 0,
              discountAmount: 0,
              creditDinnerAmount: 0,
              creditDinnerTransactionCount: 0,
              cashTransactionCount: 0,
              cardTransactionCount: 0,
              taxCollectedAmount: 0,
              discountTransactionCount: 0,
              refundTransactionCount: 0,
              refundAmount: 0
            },
            startISO: '',
            endISO: '',
            creditDinnerEnabled: false
          })
        } else {
          // Set empty data on error
          setReportData({
            salesData: [],
            productData: [],
            customerData: [],
            timeData: [],
            paymentData: [],
            summary: {
              totalRevenue: 0,
              totalOrders: 0,
              avgOrderValue: 0,
              totalCustomers: 0,
              topProduct: 'N/A',
              peakHour: 'N/A'
            }
          })
        }
      }
    } catch (error) {
      console.error('Error loading reports:', error)
      if (selectedReport === 'staff') {
        setStaffOrdersData({ byStaff: [], orders: [], startISO: '', endISO: '' })
      } else if (selectedReport === 'creditDinner') {
        setCreditDinnerReportData({ byCo: [], orders: [], startISO: '', endISO: '' })
      } else if (selectedReport === 'closing') {
        setClosingReportData({
          rows: [],
          summary: {
            openingCash: 0,
            grossSales: 0,
            netSales: 0,
            finalNetSales: 0,
            discounts: 0,
            totalSale: 0,
            totalSaleAfterDiscount: 0,
            cashSales: 0,
            cardSales: 0,
            cateringSales: 0,
            discountAmount: 0,
            creditDinnerAmount: 0,
            creditDinnerTransactionCount: 0,
            cashTransactionCount: 0,
            cardTransactionCount: 0,
            taxCollectedAmount: 0,
            discountTransactionCount: 0,
            refundTransactionCount: 0,
            refundAmount: 0
          },
          startISO: '',
          endISO: '',
          creditDinnerEnabled: false
        })
      } else {
        // Set empty data on error
        setReportData({
          salesData: [],
          productData: [],
          customerData: [],
          timeData: [],
          paymentData: [],
          summary: {
            totalRevenue: 0,
            totalOrders: 0,
            avgOrderValue: 0,
            totalCustomers: 0,
            topProduct: 'N/A',
            peakHour: 'N/A'
          }
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const getDateRangeLabel = () => {
    if (dateRange === 'custom' && customStartDate && customEndDate) {
      return `${customStartDate} to ${customEndDate}`
    }
    return dateRange.charAt(0).toUpperCase() + dateRange.slice(1)
  }

  const hasStaffDataComputed =
    !!staffOrdersData &&
    ((staffOrdersData.byStaff?.length || 0) > 0 || (staffOrdersData.orders?.length || 0) > 0)
  const staffSummaryTotals = staffOrdersData
    ? {
        orderCount: staffOrdersData.orders?.length || 0,
        revenue: staffOrdersData.byStaff?.reduce((s, r) => s + (Number(r.total_amount) || 0), 0) || 0,
        staffCount: staffOrdersData.byStaff?.length || 0
      }
    : { orderCount: 0, revenue: 0, staffCount: 0 }

  const hasCreditDinnerDataComputed =
    !!creditDinnerReportData &&
    ((creditDinnerReportData.orders?.length || 0) > 0 || (creditDinnerReportData.byCo?.length || 0) > 0)
  const creditDinnerSummaryTotals = creditDinnerReportData
    ? {
        totalAmount:
          creditDinnerReportData.byCo?.reduce((s, r) => s + (Number(r.total_credit_amount) || 0), 0) || 0,
        orderCount: creditDinnerReportData.orders?.length || 0,
        coCount: creditDinnerReportData.byCo?.length || 0
      }
    : { totalAmount: 0, orderCount: 0, coCount: 0 }

  const exportStaffToExcel = (isCSV: boolean = false) => {
    if (!staffOrdersData) return
    const workbook = XLSX.utils.book_new()
    const dateRangeLabel = getDateRangeLabel()
    const fileName = `Staff_Orders_${dateRangeLabel}_${new Date().toISOString().split('T')[0]}.${isCSV ? 'csv' : 'xlsx'}`
    const summarySheet = XLSX.utils.aoa_to_sheet([
      ['Staff orders report'],
      ['Date range', dateRangeLabel],
      ['Generated', new Date().toLocaleString()],
      [],
      ['Staff', 'Orders', 'Total amount'],
      ...staffOrdersData.byStaff.map((r) => [r.staff_name, r.order_count, r.total_amount])
    ])
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'By staff')
    const detailRows = [
      ['Order #', 'Time', 'Source', 'Placed by', 'Status', 'Total'],
      ...staffOrdersData.orders.map((o) => [
        o.order_number,
        o.order_time,
        o.order_source || '',
        o.placed_by_name,
        o.status,
        o.total_amount
      ])
    ]
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(detailRows), 'Orders')
    if (isCSV) {
      XLSX.writeFile(workbook, fileName, { bookType: 'csv' })
    } else {
      XLSX.writeFile(workbook, fileName)
    }
  }

  const exportCreditDinnerToExcel = (isCSV: boolean = false) => {
    if (!creditDinnerReportData) return
    const workbook = XLSX.utils.book_new()
    const dateRangeLabel = getDateRangeLabel()
    const fileName = `Credit_Dinner_${dateRangeLabel}_${new Date().toISOString().split('T')[0]}.${isCSV ? 'csv' : 'xlsx'}`
    const byCoSheet = XLSX.utils.aoa_to_sheet([
      ['Credit Dinner report'],
      ['Date range', dateRangeLabel],
      ['Generated', new Date().toLocaleString()],
      [],
      ['C/o name', 'Orders', 'Total credited'],
      ...creditDinnerReportData.byCo.map((r) => [r.co_name, r.order_count, r.total_credit_amount])
    ])
    // Excel worksheet names cannot contain : \ / ? * [ ]
    XLSX.utils.book_append_sheet(workbook, byCoSheet, 'ByCo')
    const detailRows = [
      [
        'Order #',
        'Time',
        'C/o',
        'Customer',
        'Payment',
        'Source',
        'Placed by',
        'Status',
        'Credit amount'
      ],
      ...creditDinnerReportData.orders.map((o) => [
        o.order_number,
        o.order_time,
        o.credit_dinner_co_name || '',
        o.credit_dinner_customer_display || o.customer_name || '',
        o.payment_method || '',
        o.order_source || '',
        o.placed_by_name || '',
        o.status,
        o.credit_dinner_amount ?? o.discount_amount ?? ''
      ])
    ]
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(detailRows), 'Orders')
    if (isCSV) {
      XLSX.writeFile(workbook, fileName, { bookType: 'csv', bookSST: false })
    } else {
      XLSX.writeFile(workbook, fileName, { bookType: 'xlsx' })
    }
  }

  const exportReport = async (format: 'excel' | 'csv') => {
    if (selectedReport === 'closing') {
      if (!closingReportData) {
        alert('No data available to export')
        return
      }
      if (format === 'excel' || format === 'csv') {
        await exportClosingToExcel(format === 'csv')
      }
      return
    }

    if (selectedReport === 'staff') {
      if (!staffOrdersData || !hasStaffDataComputed) {
        alert('No data available to export')
        return
      }
      exportStaffToExcel(format === 'csv')
      return
    }

    if (selectedReport === 'creditDinner') {
      if (!creditDinnerReportData || !hasCreditDinnerDataComputed) {
        alert('No data available to export')
        return
      }
      exportCreditDinnerToExcel(format === 'csv')
      return
    }

    if (!reportData) {
      alert('No data available to export')
      return
    }

    if (format === 'excel' || format === 'csv') {
      exportToExcel(format === 'csv')
    }
  }

  // Helper to format currency as string for Excel export
  const formatCurrencyAsString = (amount: number) => {
    const formattedAmount = parseFloat(amount.toFixed(2)).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
    const currencySymbol = restaurantSettings.currency_symbol || 'ر.س'
    if (restaurantSettings.currency_display_position === 'left') {
      return `${currencySymbol}${formattedAmount}`
    } else {
      return `${formattedAmount}${currencySymbol}`
    }
  }

  const exportToExcel = (isCSV: boolean = false) => {
    if (!reportData) return

    const workbook = XLSX.utils.book_new()
    const dateRangeLabel = getDateRangeLabel()
    const fileName = `Reports_${dateRangeLabel}_${new Date().toISOString().split('T')[0]}.${isCSV ? 'csv' : 'xlsx'}`

    // Summary Sheet
    const summaryData = [
      ['Report Summary'],
      ['Date Range', dateRangeLabel],
      ['Generated On', new Date().toLocaleString()],
      [],
      ['Metric', 'Value'],
      ['Total Revenue', formatCurrencyAsString(reportData.summary.totalRevenue)],
      ['Total Orders', reportData.summary.totalOrders],
      ['Average Order Value', formatCurrencyAsString(reportData.summary.avgOrderValue)],
      ['Total Customers', reportData.summary.totalCustomers],
      ['Top Product', reportData.summary.topProduct],
      ['Peak Hour', reportData.summary.peakHour]
    ]
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData)
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary')

    // Sales Data Sheet
    if (reportData.salesData && reportData.salesData.length > 0) {
      const salesHeaders = [['Date', 'Revenue', 'Orders', 'Customers']]
      const salesRows = reportData.salesData.map(item => [
        item.date || '',
        item.revenue || 0,
        item.orders || 0,
        item.customers || 0
      ])
      const salesData = [...salesHeaders, ...salesRows]
      const salesSheet = XLSX.utils.aoa_to_sheet(salesData)
      XLSX.utils.book_append_sheet(workbook, salesSheet, 'Sales')
    }

    // Product Data Sheet
    if (reportData.productData && reportData.productData.length > 0) {
      const productHeaders = [['Product Name', 'Sales (Quantity)', 'Revenue']]
      const productRows = reportData.productData.map(item => [
        item.name || '',
        item.sales || 0,
        item.revenue || 0
      ])
      const productData = [...productHeaders, ...productRows]
      const productSheet = XLSX.utils.aoa_to_sheet(productData)
      XLSX.utils.book_append_sheet(workbook, productSheet, 'Products')
    }

    // Customer Data Sheet
    if (reportData.customerData && reportData.customerData.length > 0) {
      const customerHeaders = [['Segment', 'Count', 'Percentage']]
      const customerRows = reportData.customerData.map(item => [
        item.segment || '',
        item.count || 0,
        `${((item.count / reportData.summary.totalCustomers) * 100).toFixed(2)}%`
      ])
      const customerData = [...customerHeaders, ...customerRows]
      const customerSheet = XLSX.utils.aoa_to_sheet(customerData)
      XLSX.utils.book_append_sheet(workbook, customerSheet, 'Customers')
    }

    // Payment Data Sheet
    if (reportData.paymentData && reportData.paymentData.length > 0) {
      const paymentHeaders = [['Payment Method', 'Count', 'Amount']]
      const paymentRows = reportData.paymentData.map(item => [
        item.method || '',
        item.count || 0,
        item.amount || 0
      ])
      const paymentData = [...paymentHeaders, ...paymentRows]
      const paymentSheet = XLSX.utils.aoa_to_sheet(paymentData)
      XLSX.utils.book_append_sheet(workbook, paymentSheet, 'Payments')
    }

    // Time Data Sheet
    if (reportData.timeData && reportData.timeData.length > 0) {
      const timeHeaders = [['Hour', 'Orders', 'Revenue']]
      const timeRows = reportData.timeData.map(item => [
        item.hour || '',
        item.orders || 0,
        item.revenue || 0
      ])
      const timeData = [...timeHeaders, ...timeRows]
      const timeSheet = XLSX.utils.aoa_to_sheet(timeData)
      XLSX.utils.book_append_sheet(workbook, timeSheet, 'Time Analysis')
    }

    // Write file
    if (isCSV) {
      // For CSV, export only the summary sheet
      XLSX.writeFile(workbook, fileName, { bookType: 'csv' })
    } else {
      XLSX.writeFile(workbook, fileName)
    }
  }

  const exportClosingToExcel = async (isCSV: boolean = false) => {
    if (!closingReportData) return
    const workbook = XLSX.utils.book_new()
    const dateRangeLabel = getDateRangeLabel()
    const fileName = `Closing_Report_${dateRangeLabel}_${new Date().toISOString().split('T')[0]}.${isCSV ? 'csv' : 'xlsx'}`
    const cdEnabled = closingReportData.creditDinnerEnabled === true
    const s = closingReportData.summary
    const disc = s.discounts ?? s.discountAmount ?? 0

    const summaryData: (string | number)[][] = [
      ['Closing Report Summary'],
      ['Date Range', dateRangeLabel],
      ['Generated On', new Date().toLocaleString()],
      [],
      ['Sales Summary'],
      ['Metric', 'Value'],
      ['Opening Cash', s.openingCash || 0],
      ['Gross Sales', s.grossSales ?? s.totalSale ?? 0],
      ['Net Sales', s.netSales ?? s.totalSale ?? 0],
      ['Final Net Sales', s.finalNetSales ?? s.totalSaleAfterDiscount ?? 0],
      ['Cash Sale', s.cashSales || 0],
      ['Card Sale', s.cardSales || 0],
      ['Catering Sale', s.cateringSales || 0],
      ['Discount Amount (excl. Credit Dinner double-count)', disc],
      ...(cdEnabled ? [['Credit Dinner Amount', s.creditDinnerAmount || 0]] : []),
      [],
      ['Transaction Summary'],
      ['Cash Transaction (Count)', s.cashTransactionCount || 0],
      ['Card Transaction (Count)', s.cardTransactionCount || 0],
      ...(cdEnabled ? [['Credit Dinner Transactions (Count)', s.creditDinnerTransactionCount || 0]] : []),
      [],
      ['Tax Summary'],
      ['Tax Collected Amount', s.taxCollectedAmount || 0],
      [],
      ['Discount Summary'],
      ['Discount Transaction (Count)', s.discountTransactionCount || 0],
      [],
      ['Refund Summary'],
      ['Refund Transaction (Count)', s.refundTransactionCount || 0],
      ['Refund Amount', s.refundAmount || 0]
    ]
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(summaryData), 'Summary')

    const rows = closingReportData.rows || []
    const baseDaily = [
      'Date',
      'Gross Sales',
      'Net Sales',
      'Final Net',
      'Cash Sales',
      'Card Sales',
      'Catering Sales',
      'Discount Amount',
      ...(cdEnabled ? ['Credit Dinner'] : []),
      'Tax Collected',
      'Refund Amount'
    ]
    const closingSheetRows = [
      baseDaily,
      ...rows.map((row) => [
        row.date || '',
        row.grossSales ?? 0,
        row.netSales ?? row.totalSale ?? 0,
        row.finalNetSales ?? row.totalSaleAfterDiscount ?? 0,
        row.cashSales || 0,
        row.cardSales || 0,
        row.cateringSales || 0,
        row.discountAmount || 0,
        ...(cdEnabled ? [row.creditDinnerAmount ?? 0] : []),
        row.taxCollectedAmount ?? 0,
        row.refundAmount ?? 0
      ])
    ]
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(closingSheetRows), 'Closing Report')

    if (!isCSV) {
      const params: { dateRange: string; startDate?: string; endDate?: string } = { dateRange }
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        params.startDate = customStartDate
        params.endDate = customEndDate
      }
      const EXPORT_CAP = 5000
      let offset = 0
      const limit = 1000
      let totalCount = Infinity
      type OrderExportRow = {
        order_number?: string
        order_time?: string
        payment_method?: string
        status?: string
        payment_status?: string
        net_line?: number
        gross_sales?: number
        cash_tender?: number
        card_tender?: number
        discount_amount?: number
        tax_amount?: number
        refund_total?: number
        refund_count?: number
        staff_name?: string | null
      }
      const allOrders: OrderExportRow[] = []
      try {
        while (offset < totalCount && allOrders.length < EXPORT_CAP) {
          const r = await apiService.getReportsOrders({ ...params, limit, offset })
          if (!r.success || !r.data) break
          const d = r.data as {
            orders?: OrderExportRow[]
            totalCount?: number
            exportRowWarningThreshold?: number
          }
          totalCount = d.totalCount ?? 0
          const batch = d.orders || []
          allOrders.push(...batch)
          if (batch.length === 0) break
          if (allOrders.length >= EXPORT_CAP) break
          offset += limit
          if (batch.length < limit) break
        }
        const warnAt = EXPORT_CAP
        if (totalCount > warnAt && allOrders.length >= warnAt) {
          window.alert(
            `Orders export is limited to ${warnAt} rows (${totalCount} orders in range). Narrow the date range for a full export.`
          )
        }
        const orderRows: (string | number | null | undefined)[][] = [
          [
            'Order #',
            'Time',
            'Payment',
            'Status',
            'Payment status',
            'Net line',
            'Gross sales',
            'Cash tender',
            'Card tender',
            'Discount',
            'Tax',
            'Refund total',
            'Refund count',
            'Staff'
          ],
          ...allOrders.map((o) => [
            o.order_number,
            o.order_time,
            o.payment_method,
            o.status,
            o.payment_status,
            o.net_line,
            o.gross_sales,
            o.cash_tender,
            o.card_tender,
            o.discount_amount,
            o.tax_amount,
            o.refund_total,
            o.refund_count,
            o.staff_name
          ])
        ]
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(orderRows), 'Orders')
      } catch (e) {
        console.error('Closing export: orders sheet failed', e)
      }
    }

    if (isCSV) {
      XLSX.writeFile(workbook, fileName, { bookType: 'csv' })
    } else {
      XLSX.writeFile(workbook, fileName)
    }
  }


  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658']

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  const hasClosingData = closingReportData != null
  const standardReportData: ReportData = reportData ?? {
    salesData: [],
    productData: [],
    customerData: [],
    timeData: [],
    paymentData: [],
    summary: {
      totalRevenue: 0,
      totalOrders: 0,
      avgOrderValue: 0,
      totalCustomers: 0,
      topProduct: 'N/A',
      peakHour: 'N/A'
    }
  }
  if (
    (selectedReport === 'staff' && !hasStaffDataComputed) ||
    (selectedReport === 'creditDinner' && !hasCreditDinnerDataComputed) ||
    (selectedReport !== 'closing' &&
      selectedReport !== 'staff' &&
      selectedReport !== 'creditDinner' &&
      (!reportData || reportData.summary.totalOrders === 0)) ||
    (selectedReport === 'closing' && !hasClosingData)
  ) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl sm:truncate">
              Reports & Analytics
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Analyze restaurant's performance and make data-driven decisions
            </p>
          </div>
        </div>

        {/* Date Range Selector */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <div className="flex items-center space-x-4">
            <CalendarIcon className="h-5 w-5 text-gray-500" />
            <div className="flex space-x-2">
              {['today', 'week', 'month', 'year'].map(range => (
                <button
                  key={range}
                  onClick={() => setDateRange(range as any)}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors capitalize ${
                    dateRange === range
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  {range}
                </button>
              ))}
              <button
                onClick={() => setDateRange('custom')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  dateRange === 'custom'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                Custom
              </button>
            </div>
            {dateRange === 'custom' && (
              <div className="flex items-center space-x-2">
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            )}
          </div>
        </div>

        {/* Empty State */}
        <div className="text-center py-12 bg-white dark:bg-gray-800 shadow rounded-lg">
          <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No data available
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            No orders found for the selected date range. Try selecting a different period.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl sm:truncate">
            Reports & Analytics
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Analyze restaurant's performance and make data-driven decisions
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
          <button
            onClick={() => void exportReport('excel')}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <DocumentArrowDownIcon className="-ml-1 mr-2 h-5 w-5" />
            Export Excel
          </button>
        </div>
      </div>

      {/* Date Range Selector */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="flex items-center space-x-4">
          <CalendarIcon className="h-5 w-5 text-gray-500" />
          <div className="flex space-x-2">
            {['today', 'week', 'month', 'year'].map(range => (
              <button
                key={range}
                onClick={() => setDateRange(range as any)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors capitalize ${
                  dateRange === range
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {range}
              </button>
            ))}
            <button
              onClick={() => setDateRange('custom')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                dateRange === 'custom'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              Custom
            </button>
          </div>
          {dateRange === 'custom' && (
            <div className="flex items-center space-x-2">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      {selectedReport === 'staff' ? (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard
            title="Orders (in range)"
            value={staffSummaryTotals.orderCount}
            icon={ShoppingCartIcon}
            color="blue"
            formatCurrency={formatCurrency}
          />
          <StatCard
            title="Total (staff view)"
            value={staffSummaryTotals.revenue}
            icon={CurrencyDollarIcon}
            color="green"
            isCurrency={true}
            formatCurrency={formatCurrency}
          />
          <StatCard
            title="Staff with orders"
            value={staffSummaryTotals.staffCount}
            icon={UserIcon}
            color="indigo"
            formatCurrency={formatCurrency}
          />
        </div>
      ) : selectedReport === 'creditDinner' ? (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard
            title="Credit Dinner total"
            value={creditDinnerSummaryTotals.totalAmount}
            icon={GiftIcon}
            color="green"
            isCurrency={true}
            formatCurrency={formatCurrency}
          />
          <StatCard
            title="Orders"
            value={creditDinnerSummaryTotals.orderCount}
            icon={ShoppingCartIcon}
            color="blue"
            formatCurrency={formatCurrency}
          />
          <StatCard
            title="Distinct C/o names"
            value={creditDinnerSummaryTotals.coCount}
            icon={UserIcon}
            color="indigo"
            formatCurrency={formatCurrency}
          />
        </div>
      ) : selectedReport === 'closing' ? (
        <div className="space-y-6">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Sales Summary</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 max-w-4xl">
              Cash and card amounts are tender recorded at sale (including split payments). Refunds are not subtracted from those buckets; they appear in Refund Summary. Net sales is the sum of qualifying order totals; final net sales subtracts refunds whose timestamp falls in this period.
            </p>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard title="Opening Cash" value={closingReportData?.summary.openingCash || 0} icon={CurrencyDollarIcon} color="gray" isCurrency={true} formatCurrency={formatCurrency} />
              <StatCard title="Gross Sales" value={closingReportData?.summary.grossSales ?? closingReportData?.summary.totalSale ?? 0} icon={CurrencyDollarIcon} color="violet" isCurrency={true} formatCurrency={formatCurrency} />
              <StatCard title="Net Sales" value={closingReportData?.summary.netSales ?? closingReportData?.summary.totalSale ?? 0} icon={CurrencyDollarIcon} color="green" isCurrency={true} formatCurrency={formatCurrency} />
              <StatCard title="Final Net Sales" value={closingReportData?.summary.finalNetSales ?? closingReportData?.summary.totalSaleAfterDiscount ?? 0} icon={CurrencyDollarIcon} color="rose" isCurrency={true} formatCurrency={formatCurrency} />
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mt-3">
              <StatCard title="Cash Sale" value={closingReportData?.summary.cashSales || 0} icon={CurrencyDollarIcon} color="blue" isCurrency={true} formatCurrency={formatCurrency} />
              <StatCard title="Card Sale" value={closingReportData?.summary.cardSales || 0} icon={CurrencyDollarIcon} color="indigo" isCurrency={true} formatCurrency={formatCurrency} />
              <StatCard title="Catering Sale" value={closingReportData?.summary.cateringSales || 0} icon={ShoppingCartIcon} color="purple" isCurrency={true} formatCurrency={formatCurrency} />
              <StatCard title="Discount Amount" value={closingReportData?.summary.discounts ?? closingReportData?.summary.discountAmount ?? 0} icon={DocumentArrowDownIcon} color="blue" isCurrency={true} formatCurrency={formatCurrency} />
            </div>
            {closingReportData?.creditDinnerEnabled === true && (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mt-3">
                <StatCard
                  title="Credit Dinner Amount"
                  value={closingReportData?.summary.creditDinnerAmount || 0}
                  icon={GiftIcon}
                  color="amber"
                  isCurrency={true}
                  formatCurrency={formatCurrency}
                />
              </div>
            )}
          </div>

          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Transaction Summary</h3>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <StatCard title="Cash Transaction (Count)" value={closingReportData?.summary.cashTransactionCount || 0} icon={ShoppingCartIcon} color="blue" formatCurrency={formatCurrency} />
              <StatCard title="Card Transaction (Count)" value={closingReportData?.summary.cardTransactionCount || 0} icon={ShoppingCartIcon} color="indigo" formatCurrency={formatCurrency} />
              {closingReportData?.creditDinnerEnabled === true && (
                <StatCard
                  title="Credit Dinner Transactions (Count)"
                  value={closingReportData?.summary.creditDinnerTransactionCount || 0}
                  icon={GiftIcon}
                  color="amber"
                  formatCurrency={formatCurrency}
                />
              )}
            </div>
          </div>

          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Tax Summary</h3>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <StatCard title="Tax Collected Amount" value={closingReportData?.summary.taxCollectedAmount || 0} icon={CurrencyDollarIcon} color="emerald" isCurrency={true} formatCurrency={formatCurrency} />
            </div>
          </div>

          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Discount Summary</h3>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <StatCard title="Discount Transaction (Count)" value={closingReportData?.summary.discountTransactionCount || 0} icon={DocumentArrowDownIcon} color="orange" formatCurrency={formatCurrency} />
              <StatCard title="Discount Amount" value={closingReportData?.summary.discounts ?? closingReportData?.summary.discountAmount ?? 0} icon={DocumentArrowDownIcon} color="orange" isCurrency={true} formatCurrency={formatCurrency} />
            </div>
          </div>

          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Refund Summary</h3>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <StatCard title="Refund Transaction (Count)" value={closingReportData?.summary.refundTransactionCount || 0} icon={DocumentArrowDownIcon} color="red" formatCurrency={formatCurrency} />
              <StatCard title="Refund Amount" value={closingReportData?.summary.refundAmount || 0} icon={CurrencyDollarIcon} color="red" isCurrency={true} formatCurrency={formatCurrency} />
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Total Revenue"
            value={reportData?.summary.totalRevenue || 0}
            icon={CurrencyDollarIcon}
            color="green"
            isCurrency={true}
            formatCurrency={formatCurrency}
          />
          <StatCard
            title="Total Orders"
            value={reportData?.summary.totalOrders || 0}
            icon={ShoppingCartIcon}
            color="blue"
            formatCurrency={formatCurrency}
          />
          <StatCard
            title="Avg Order Value"
            value={reportData?.summary.avgOrderValue || 0}
            icon={ChartBarIcon}
            color="purple"
            isCurrency={true}
            formatCurrency={formatCurrency}
          />
          <StatCard
            title="Total Customers"
            value={reportData?.summary.totalCustomers || 0}
            icon={UserGroupIcon}
            color="indigo"
            formatCurrency={formatCurrency}
          />
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="flex space-x-1 bg-gray-200 dark:bg-gray-700 rounded-lg p-1">
          {[
            { key: 'sales', label: 'Sales Report', icon: CurrencyDollarIcon },
            { key: 'products', label: 'Product Performance', icon: ChartBarIcon },
            { key: 'customers', label: 'Customer Analytics', icon: UserGroupIcon },
            { key: 'time', label: 'Time Analysis', icon: ClockIcon },
            { key: 'staff', label: 'Staff orders', icon: UserIcon },
            ...(creditDinnerEnabled ? [{ key: 'creditDinner' as const, label: 'Credit Dinner', icon: GiftIcon }] : []),
            { key: 'closing', label: 'Closing Report', icon: DocumentArrowDownIcon }
          ].map(report => (
            <button
              key={report.key}
              onClick={() => setSelectedReport(report.key as any)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center ${
                selectedReport === report.key
                  ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <report.icon className="h-4 w-4 mr-2" />
              {report.label}
            </button>
          ))}
        </div>
      </div>

      {/* Staff orders */}
      {selectedReport === 'staff' && staffOrdersData && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">By staff member</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Staff</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Orders</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {(staffOrdersData.byStaff || []).map((row, idx) => (
                    <tr key={row.created_by || `unknown-${idx}`}>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{row.staff_name}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100">{row.order_count}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100">{formatCurrency(row.total_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Order detail</h3>
            <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Order #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Time</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Source</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Placed by</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {(staffOrdersData.orders || []).map((o) => (
                    <tr key={o.id}>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{o.order_number}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                        {o.order_time ? new Date(o.order_time).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 capitalize">{o.order_source || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{o.placed_by_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 capitalize">{o.status}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100">{formatCurrency(Number(o.total_amount) || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Credit Dinner */}
      {selectedReport === 'creditDinner' && creditDinnerReportData && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Filters</h3>
            <div className="flex flex-wrap gap-4 items-end">
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">C/o contains</label>
                <input
                  type="text"
                  value={creditDinnerCoInput}
                  onChange={(e) => setCreditDinnerCoInput(e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-w-[200px]"
                  placeholder="Care-of name"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Customer contains</label>
                <input
                  type="text"
                  value={creditDinnerCustomerInput}
                  onChange={(e) => setCreditDinnerCustomerInput(e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-w-[200px]"
                  placeholder="Optional"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  setCreditDinnerCoApplied(creditDinnerCoInput.trim())
                  setCreditDinnerCustomerApplied(creditDinnerCustomerInput.trim())
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
              >
                Apply filters
              </button>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">By C/o name</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">C/o</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Orders</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Total credited</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {(creditDinnerReportData.byCo || []).map((row, idx) => (
                    <tr key={`${row.co_name}-${idx}`}>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{row.co_name}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100">{row.order_count}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100">
                        {formatCurrency(row.total_credit_amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Order detail</h3>
            <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Order #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Time</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">C/o</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Payment</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Source</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Placed by</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Credit amount</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {(creditDinnerReportData.orders || []).map((o) => (
                    <tr key={o.id}>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{o.order_number}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                        {o.order_time ? new Date(o.order_time).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{o.credit_dinner_co_name || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                        {o.credit_dinner_customer_display || o.customer_name || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 capitalize">{o.payment_method || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 capitalize">{o.order_source || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{o.placed_by_name || '—'}</td>
                      <td className="px-4 py-3 text-sm text-right text-amber-600 dark:text-amber-400">
                        {formatCurrency(Number(o.credit_dinner_amount ?? o.discount_amount ?? 0) || 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Sales Report */}
      {selectedReport === 'sales' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Revenue Trend
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={standardReportData.salesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#6B7280" />
                <YAxis stroke="#6B7280" />
                <Tooltip 
                  formatter={(value: any) => [formatCurrency(Number(value)), 'Revenue']}
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    color: '#F9FAFB'
                  }}
                />
                <Area type="monotone" dataKey="revenue" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.3} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Orders Trend
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={standardReportData.salesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#6B7280" />
                <YAxis stroke="#6B7280" />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    color: '#F9FAFB'
                  }}
                />
                <Line type="monotone" dataKey="orders" stroke="#10B981" strokeWidth={2} />
                <Line type="monotone" dataKey="customers" stroke="#F59E0B" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Product Performance */}
      {selectedReport === 'products' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Top Products by Revenue
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={standardReportData.productData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="name" stroke="#6B7280" />
                <YAxis stroke="#6B7280" />
                <Tooltip 
                  formatter={(value: any) => [formatCurrency(Number(value)), 'Revenue']}
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    color: '#F9FAFB'
                  }}
                />
                <Bar dataKey="revenue" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Top Products by Sales
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={standardReportData.productData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="name" stroke="#6B7280" />
                <YAxis stroke="#6B7280" />
                <Tooltip 
                  formatter={(value: any) => [value, 'Sales']}
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    color: '#F9FAFB'
                  }}
                />
                <Bar dataKey="sales" fill="#10B981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Customer Analytics */}
      {selectedReport === 'customers' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Customer Segments
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={standardReportData.customerData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ segment, percentage }) => `${segment} ${percentage}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {standardReportData.customerData.map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name) => [`${value} customers`, name]}
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    color: '#F9FAFB'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Payment Methods
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={standardReportData.paymentData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="method" stroke="#6B7280" />
                <YAxis stroke="#6B7280" />
                <Tooltip 
                  formatter={(value: any, name: string) => [
                    name === 'count' ? `${value} transactions` : formatCurrency(Number(value)),
                    name === 'count' ? 'Count' : 'Amount'
                  ]}
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    color: '#F9FAFB'
                  }}
                />
                <Bar dataKey="count" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Time Analysis */}
      {selectedReport === 'time' && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Hourly Performance
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={standardReportData.timeData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="hour" stroke="#6B7280" />
              <YAxis yAxisId="left" stroke="#6B7280" />
              <YAxis yAxisId="right" orientation="right" stroke="#6B7280" />
              <Tooltip 
                formatter={(value: any, name: string) => {
                  if (name === 'revenue') {
                    return [formatCurrency(Number(value)), 'Revenue']
                  }
                  return [value, name === 'orders' ? 'Orders' : name]
                }}
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#F9FAFB'
                }}
              />
              <Area yAxisId="left" type="monotone" dataKey="orders" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.3} />
              <Area yAxisId="right" type="monotone" dataKey="revenue" stroke="#10B981" fill="#10B981" fillOpacity={0.3} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Closing Report */}
      {selectedReport === 'closing' && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Closing Report (Date-wise)</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Gross Sales</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Net Sales</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Final Net</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Cash Sales</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Card Sales</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Catering Sales</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Discount Amount</th>
                  {closingReportData?.creditDinnerEnabled === true && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Credit Dinner</th>
                  )}
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Tax Collected</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Refunds</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {(closingReportData?.rows || []).map((row) => (
                  <tr key={row.date}>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{row.date}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100">{formatCurrency(row.grossSales ?? 0)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100">{formatCurrency(row.netSales ?? row.totalSale ?? 0)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100">{formatCurrency(row.finalNetSales ?? row.totalSaleAfterDiscount)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100">{formatCurrency(row.cashSales)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100">{formatCurrency(row.cardSales)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100">{formatCurrency(row.cateringSales)}</td>
                    <td className="px-4 py-3 text-sm text-right text-red-600 dark:text-red-400">{formatCurrency(row.discountAmount)}</td>
                    {closingReportData?.creditDinnerEnabled === true && (
                      <td className="px-4 py-3 text-sm text-right text-amber-600 dark:text-amber-400">
                        {formatCurrency(row.creditDinnerAmount ?? 0)}
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100">{formatCurrency(row.taxCollectedAmount ?? 0)}</td>
                    <td className="px-4 py-3 text-sm text-right text-red-600 dark:text-red-400">{formatCurrency(row.refundAmount ?? 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Key Insights */}
      {selectedReport !== 'closing' && selectedReport !== 'staff' && selectedReport !== 'creditDinner' && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Key Insights
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="p-4 bg-blue-50 dark:bg-blue-900 rounded-lg">
              <h4 className="font-medium text-blue-900 dark:text-blue-200 mb-2">Top Product</h4>
              <p className="text-blue-800 dark:text-blue-300">{standardReportData.summary.topProduct}</p>
            </div>
            <div className="p-4 bg-green-50 dark:bg-green-900 rounded-lg">
              <h4 className="font-medium text-green-900 dark:text-green-200 mb-2">Peak Hour</h4>
              <p className="text-green-800 dark:text-green-300">{standardReportData.summary.peakHour}</p>
            </div>
            <div className="p-4 bg-purple-50 dark:bg-purple-900 rounded-lg">
              <h4 className="font-medium text-purple-900 dark:text-purple-200 mb-2">Avg Order Value</h4>
              <p className="text-purple-800 dark:text-purple-300">{formatCurrency(standardReportData.summary.avgOrderValue)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Reports
